// src/components/JobItem.js
import React, { useState, useMemo, useEffect } from "react";
import "./JobItem.css";
import placeholderImage from "../assets/home.svg";
import axios from "axios";

const formatTimeDifference = (date) => {
  const now = new Date();
  const differenceInMs = now - new Date(date);
  const differenceInHours = Math.floor(differenceInMs / (1000 * 60 * 60));
  const differenceInDays = Math.floor(differenceInHours / 24);

  if (differenceInDays > 0) {
    return `${differenceInDays}d`;
  } else {
    return `${differenceInHours}h`;
  }
};

const toArray = (value) =>
  Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];

const getPlainTextFromHtml = (html = "") => {
  if (!html) return "";

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body?.textContent?.replace(/\s+/g, " ").trim() || "";
};

const inferDomainFromHtml = (html = "") => {
  const match = html.match(/https?:\/\/([^\s"'<>]+)/i);
  if (!match?.[1]) return "";
  return match[1].replace(/^www\./i, "").split("/")[0];
};

const getCompanyInitials = (companyName = "") => {
  const parts = companyName.split(/\s+/).filter(Boolean).slice(0, 2);
  if (!parts.length) return "JR";
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
};

const findNearestListItems = (element) => {
  let current = element;

  while (current) {
    let sibling = current.nextElementSibling;
    while (sibling) {
      if (sibling.matches("ul, ol")) {
        return [...sibling.querySelectorAll("li")]
          .map((item) => item.textContent?.trim())
          .filter(Boolean);
      }
      sibling = sibling.nextElementSibling;
    }
    current = current.parentElement;
  }

  return [];
};

const extractSectionItemsFromHtml = (html = "", keywords = []) => {
  if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") {
    return [];
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const elements = [...doc.querySelectorAll("strong, h1, h2, h3, h4, h5, h6, p")];

  for (const element of elements) {
    const text = element.textContent?.toLowerCase().trim() || "";
    if (!text) continue;
    if (keywords.some((keyword) => text.includes(keyword))) {
      const items = findNearestListItems(element);
      if (items.length) {
        return items;
      }
    }
  }

  return [];
};

const JobItem = ({
  job,
  refreshJob,
  removeJob,
  userId,
  isRefreshingScores = false,
  activeCriteriaSignature = "",
  rank = null,
  showRank = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);
  const [logoUnavailable, setLogoUnavailable] = useState(false);
  const [showOriginalDescription, setShowOriginalDescription] = useState(false);
  const [showMatchScore, setShowMatchScore] = useState(false);

  const companyDomain = useMemo(
    () => job.domain || inferDomainFromHtml(job.content || ""),
    [job.domain, job.content]
  );

  const companySummary = useMemo(
    () => job.description || getPlainTextFromHtml(job.content || "").slice(0, 320),
    [job.description, job.content]
  );

  const normalizedTerms = useMemo(
    () => (Array.isArray(job.terms) ? job.terms.join(", ") : job.terms),
    [job.terms]
  );

  const normalizedContractType = useMemo(
    () =>
      Array.isArray(job.contract_type)
        ? job.contract_type.join(", ")
        : job.contract_type,
    [job.contract_type]
  );

  const normalizedWorkload = useMemo(
    () => (Array.isArray(job.workload) ? job.workload.join(", ") : job.workload),
    [job.workload]
  );

  const keyTasks = useMemo(
    () =>
      toArray(job.job?.keyTasks).length
        ? toArray(job.job?.keyTasks)
        : extractSectionItemsFromHtml(job.content || "", [
            "das bewegen sie",
            "aufgaben",
            "responsibilities",
            "your mission",
            "key tasks",
          ]),
    [job.job?.keyTasks, job.content]
  );

  const skills = useMemo(
    () =>
      toArray(job.job?.skills).length
        ? toArray(job.job?.skills)
        : extractSectionItemsFromHtml(job.content || "", [
            "das bringen sie mit",
            "anforderungen",
            "qualifications",
            "requirements",
            "skills",
            "profil",
          ]),
    [job.job?.skills, job.content]
  );

  const education = useMemo(
    () => toArray(job.job?.education),
    [job.job?.education]
  );

  const yearsOfExperience = useMemo(
    () => toArray(job.job?.yearsOfExperience),
    [job.job?.yearsOfExperience]
  );

  const benefits = useMemo(
    () =>
      toArray(job.job?.benefits).length
        ? toArray(job.job?.benefits)
        : extractSectionItemsFromHtml(job.content || "", [
            "our benefits",
            "unsere vorteile",
            "benefits",
            "what we offer",
          ]),
    [job.job?.benefits, job.content]
  );

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const needsCompanyInfo = !job.description && !job.industry && !job.foundedYear;
  const needsJobDetails = !job.job?.keyTasks?.length && !job.job?.skills?.length;

  useEffect(() => {
    if (!expanded) return;
    if (needsCompanyInfo && !loadingCompanyInfo) {
      handleFetchCompanyInfo();
    }
    if (needsJobDetails && !loadingJobDetails) {
      handleFetchJobDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleFetchCompanyInfo = async () => {
    setLoadingCompanyInfo(true);
    try {
      const response = await axios.post("/.netlify/functions/fetchCompanyInfo", {
        jobId: job._id,
        company_name: job.company_name,
        location: job.location,
      });
      if (response.status === 200) {
        await refreshJob(job._id); // Ensure refreshJob is awaited
      }
    } catch (error) {
      console.error("Error fetching company info:", error);
    } finally {
      setLoadingCompanyInfo(false);
    }
  };

  const handleFetchJobDetails = async () => {
    setLoadingJobDetails(true);
    try {
      const response = await axios.post("/.netlify/functions/fetchJobDetails", {
        jobId: job._id,
      });
      if (response.status === 200) {
        await refreshJob(job._id); // Ensure refreshJob is awaited
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
    } finally {
      setLoadingJobDetails(false);
    }
  };

  const handleDeleteJob = async () => {
    try {
      const response = await axios.post("/.netlify/functions/deleteJob", {
        jobId: job._id,
      });
      if (response.status === 200) {
        removeJob(job._id); // Remove job from the UI
      }
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const numericScoreValue = useMemo(() => {
    const rawScore =
      job.score_user?.score ??
      job.score_user?.total_score ??
      job.score_user?.totalScore ??
      job.score_user?.["Total Score"] ??
      null;
    const nextScore = Number(rawScore);
    return Number.isFinite(nextScore) ? nextScore : null;
  }, [job.score_user]);

  const totalScore = useMemo(
    () => numericScoreValue ?? "N/A",
    [numericScoreValue]
  );

  const hasNumericScore = useMemo(
    () => Number.isFinite(numericScoreValue),
    [numericScoreValue]
  );

  const scoreProgressDegrees = useMemo(() => {
    if (!hasNumericScore) {
      return "0deg";
    }

    const clampedScore = Math.max(0, Math.min(10, Number(totalScore)));
    return `${(clampedScore / 10) * 360}deg`;
  }, [hasNumericScore, totalScore]);

  const scoreRingStyle = useMemo(
    () => ({ "--score-progress": scoreProgressDegrees }),
    [scoreProgressDegrees]
  );

  const scoreSignature = useMemo(
    () => JSON.stringify(job.score_user?.criteria_snapshot || []),
    [job.score_user?.criteria_snapshot]
  );

  const isScoreStale = useMemo(
    () =>
      Boolean(
        activeCriteriaSignature &&
          scoreSignature &&
          scoreSignature !== "[]" &&
          activeCriteriaSignature !== scoreSignature
      ),
    [activeCriteriaSignature, scoreSignature]
  );

  const isScorePending = useMemo(
    () => Boolean(userId) && (isRefreshingScores && (!hasNumericScore || isScoreStale)),
    [userId, isRefreshingScores, hasNumericScore, isScoreStale]
  );

  const scoreBreakdown = useMemo(() => {
    const details = job.score_user?.details || {};
    const explanations = job.score_user?.explanations || {};
    const snapshotTitles = Array.isArray(job.score_user?.criteria_snapshot)
      ? job.score_user.criteria_snapshot
          .map((criterion) => criterion?.title)
          .filter(Boolean)
      : [];
    const orderedTitles = [
      ...snapshotTitles,
      ...Object.keys(details).filter((title) => !snapshotTitles.includes(title)),
    ];

    return orderedTitles
      .map((title) => {
        const numericScore = Number(details[title]);
        const explanation = explanations[title] || "";

        if (!Number.isFinite(numericScore) && !explanation) {
          return null;
        }

        return {
          title,
          score: Number.isFinite(numericScore) ? numericScore : null,
          explanation,
        };
      })
      .filter(Boolean);
  }, [job.score_user]);

  const scoreReasoning = useMemo(
    () => job.score_user?.reasoning || "",
    [job.score_user?.reasoning]
  );

  const shouldShowRank = showRank && hasNumericScore && Number.isInteger(rank);

  return (
    <li className={`job-item ${expanded ? "expanded" : ""}`}>
      <div className="job-summary" onClick={toggleExpand}>
        <div className="job-summary-main">
          <div className="logo-container">
            {companyDomain && !logoUnavailable ? (
              <img
                src={`https://logo.clearbit.com/${companyDomain}`}
                alt={`${job.company_name} logo`}
                onError={() => {
                  setLogoUnavailable(true);
                }}
                className="company-logo"
              />
            ) : (
              <div className="company-logo company-logo-fallback">
                {job.company_name ? getCompanyInitials(job.company_name) : <img src={placeholderImage} alt="Company placeholder" className="company-logo company-logo-placeholder" />}
              </div>
            )}
          </div>
          <div className="job-info">
            <h2>{job.company_name || ""}</h2>
            <h3>{job.title || ""}</h3>
          </div>
        </div>
        <div className="job-summary-meta">
          <div className="score-column">
            {shouldShowRank && (
              <div className={`rank-chip${rank === 1 ? " rank-chip--top" : ""}`}>
                {rank === 1 ? "Top match" : `#${rank}`}
              </div>
            )}
          {userId && (
            <div className={`score-pill${isScorePending ? " score-pill--pending" : hasNumericScore ? " score-pill--ready" : ""}`}>
              {isScorePending ? (
                <>
                  <span className="mini-spinner" aria-hidden="true"></span>
                  <span className="score-pill-label">Scoring</span>
                </>
              ) : (
                <>
                  <span className="score-pill-value-ring" style={scoreRingStyle}>
                    <span className="score-pill-value">{hasNumericScore ? Math.round(Number(totalScore)) : "--"}</span>
                  </span>
                  <span className="score-pill-label">Match</span>
                </>
              )}
            </div>
          )}
          </div>
          <p className="publication-date">
            {formatTimeDifference(job.publication_date)}
          </p>
        </div>
      </div>
      {expanded && (
        <div className="job-details">
          {userId && (hasNumericScore || isScorePending || scoreBreakdown.length > 0 || scoreReasoning) && (
            <>
              <div className="detail-section score-breakdown-section">
                <h4
                  className="collapsible-heading"
                  onClick={() => setShowMatchScore(!showMatchScore)}
                >
                  Match Score <span className="collapse-indicator">{showMatchScore ? "▲" : "▼"}</span>
                </h4>
                {showMatchScore && (
                  <>
                    <div className="score-breakdown-summary">
                      <div className={`score-pill score-pill--detail${isScorePending ? " score-pill--pending" : hasNumericScore ? " score-pill--ready" : ""}`}>
                        {isScorePending ? (
                          <>
                            <span className="mini-spinner" aria-hidden="true"></span>
                            <span className="score-pill-label">Scoring</span>
                          </>
                        ) : (
                          <>
                            <span className="score-pill-value-ring" style={scoreRingStyle}>
                              <span className="score-pill-value">{hasNumericScore ? Math.round(Number(totalScore)) : "--"}</span>
                            </span>
                            <span className="score-pill-label">Overall match</span>
                          </>
                        )}
                      </div>
                      {scoreReasoning && <p className="score-reasoning">{scoreReasoning}</p>}
                    </div>
                    {scoreBreakdown.length > 0 && (
                      <div className="score-breakdown-grid">
                        {scoreBreakdown.map((item) => (
                          <div key={item.title} className="score-breakdown-card">
                            <div className="score-breakdown-card-header">
                              <span className="score-breakdown-title">{item.title}</span>
                              <span className="score-breakdown-value">
                                {item.score !== null ? Math.round(item.score) : "--"}/10
                              </span>
                            </div>
                            {item.explanation && (
                              <p className="score-breakdown-explanation">{item.explanation}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <hr />
            </>
          )}
          {(loadingCompanyInfo || loadingJobDetails) && (
            <div className="auto-fetch-banner">
              <div className="auto-fetch-spinner" />
              <span>
                Loading {loadingCompanyInfo && loadingJobDetails ? "company info & job details" : loadingCompanyInfo ? "company info" : "job details"}…
              </span>
            </div>
          )}
          <div className="detail-section">
            <h4>Company</h4>
            <div className="detail-group">
              {companySummary && (
                <div className="detail-item">
                  <span>Description</span>
                  <p>{companySummary}</p>
                </div>
              )}
              {job.industry && (
                <div className="detail-item">
                  <span>Industry</span>
                  <p>{job.industry}</p>
                </div>
              )}
              {companyDomain && (
                <div className="detail-item">
                  <span>Website</span>
                  <p>
                    <a
                      href={`https://${companyDomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {companyDomain}
                    </a>
                  </p>
                </div>
              )}
              {job.foundedYear && (
                <div className="detail-item">
                  <span>Founded Year</span>
                  <p>{job.foundedYear}</p>
                </div>
              )}
              {job.metrics?.annualRevenue && (
                <div className="detail-item">
                  <span>Annual Revenue</span>
                  <p>{job.metrics.annualRevenue}</p>
                </div>
              )}
              {job.metrics?.employees && (
                <div className="detail-item">
                  <span>Employees</span>
                  <p>{job.metrics.employees}</p>
                </div>
              )}
              {job.ceo && (
                <div className="detail-item">
                  <span>CEO</span>
                  <p>{job.ceo}</p>
                </div>
              )}
              {job.competitors && (
                <div className="detail-item">
                  <span>Competitors</span>
                  <p>{job.competitors.join(", ")}</p>
                </div>
              )}
              {job.parent && (
                <div className="detail-item">
                  <span>Parent</span>
                  <p>{job.parent}</p>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Job</h4>
            <div className="detail-group">
              {job.title && (
                <div className="detail-item">
                  <span>Title</span>
                  <p>{job.title}</p>
                </div>
              )}
              {normalizedTerms && (
                <div className="detail-item">
                  <span>Term</span>
                  <p>{normalizedTerms}</p>
                </div>
              )}
              {job.location && (
                <div className="detail-item">
                  <span>Location</span>
                  <p>
                    {job.location.city}, {job.location.street},{" "}
                    {job.location.postalCode}
                  </p>
                </div>
              )}
              {normalizedContractType && (
                <div className="detail-item">
                  <span>Contract Type</span>
                  <p>{normalizedContractType}</p>
                </div>
              )}
              {normalizedWorkload && (
                <div className="detail-item">
                  <span>Workload</span>
                  <p>{normalizedWorkload}</p>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Key Tasks</h4>
            <div className="detail-group full-width">
              {keyTasks.length > 0 && (
                <div className="detail-item">
                  <ul>
                    {keyTasks.map((task, index) => (
                      <li key={index}>{task}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Skills</h4>
            <div className="detail-group full-width">
              {skills.length > 0 && (
                <div className="detail-item">
                  <ul>
                    {skills.map((skill, index) => (
                      <li key={index}>{skill}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Education</h4>
            <div className="detail-group full-width">
              {education.length > 0 && (
                <div className="detail-item">
                  <ul>
                    {education.map((edu, index) => (
                      <li key={index}>{edu}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Years of Experience</h4>
            <div className="detail-group full-width">
              {yearsOfExperience.length > 0 && (
                <div className="detail-item">
                  <ul>
                    {yearsOfExperience.map((exp, index) => (
                      <li key={index}>{exp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className="detail-section">
            <h4>Benefits</h4>
            <div className="detail-group full-width">
              {benefits.length > 0 && (
                <div className="detail-item">
                  <ul>
                    {benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <hr />
          {job.content && (
            <>
              <div className="detail-section">
                <h4
                  className="collapsible-heading"
                  onClick={() => setShowOriginalDescription(!showOriginalDescription)}
                >
                  Original Job Description <span className="collapse-indicator">{showOriginalDescription ? "▲" : "▼"}</span>
                </h4>
                {showOriginalDescription && (
                  <div className="detail-group full-width">
                    <div
                      className="detail-item detail-html-content"
                      dangerouslySetInnerHTML={{ __html: job.content }}
                    />
                  </div>
                )}
              </div>
              <hr />
            </>
          )}
          {/* Action buttons */}
          <div className="button-container">
            <a
              className="styled-button app-button app-button--primary apply-button"
              href={`https://www.google.com/search?q=${encodeURIComponent(`${job.title || ""} ${job.company_name || ""} apply powered by mymon.day`)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Apply
            </a>
            <button
              className="styled-button app-button app-button--danger delete-button"
              onClick={handleDeleteJob}
            >
              Delete Job
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

export default JobItem;